import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getRunningEntries } from '../api/timeEntry.api';

const TimerContext = createContext(null);

/**
 * TimerProvider — globally tracks whether the current user has any running
 * time-entry timers. Polls every 30 s so other screens always have fresh data.
 *
 * Exposes:
 *   hasRunningTimer  : boolean
 *   runningEntries   : array
 *   refreshTimer()   : call to force an immediate re-fetch (e.g. after stop/start)
 */
export const TimerProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [runningEntries, setRunningEntries] = useState([]);

  const employeeUUId = user?.sapEmployeeUUID || user?.id || '';

  const fetchRunning = useCallback(async () => {
    if (!isAuthenticated || !employeeUUId) {
      setRunningEntries([]);
      return;
    }
    try {
      const res = await getRunningEntries(employeeUUId);
      if (res.isSuccess && res.data) {
        setRunningEntries(Array.isArray(res.data) ? res.data : [res.data]);
      } else {
        setRunningEntries([]);
      }
    } catch {
      // silent – network errors shouldn't break the UI
    }
  }, [isAuthenticated, employeeUUId]);

  useEffect(() => {
    fetchRunning();
    const interval = setInterval(fetchRunning, 30_000);
    return () => clearInterval(interval);
  }, [fetchRunning]);

  return (
    <TimerContext.Provider
      value={{
        runningEntries,
        hasRunningTimer: runningEntries.length > 0,
        refreshTimer: fetchRunning,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => useContext(TimerContext);
